import React, { useEffect } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Box, Button, Chip, Stack, Grid, Divider } from "@mui/material";
import { ArrowBack, Send, Edit, Delete } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useCampaignDetails, useCampaignActions, statusColors, typeColors } from "./hooks/useCampaign";
import { Loading, Section, StatsDisplay, ChannelStats, MessagePreview, TargetAudienceDisplay } from "./components/CampaignComponents";
import CampaignResults from "./components/CampaignResults";
import "./Campaign.css";

const CampaignDetails = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const { campaign, loading, fetch } = useCampaignDetails();
  const { execute, remove } = useCampaignActions(() => fetch(campaignId));

  useEffect(() => { fetch(campaignId).then(r => !r.success && navigate("/campaigns")); }, [campaignId]);

  const handleDelete = async () => { if (await remove(campaignId)) navigate("/campaigns"); };

  if (loading) return <Loading />;
  if (!campaign) return <Box sx={{ p: 3 }}>Campaign not found</Box>;

  return (
    <Box className="campaign-container">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Header */}
      <Box className="campaign-header">
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/campaigns")}>Back</Button>
        <Stack direction="row" spacing={1}>
          {campaign.status === "draft" && (
            <>
              <Button variant="outlined" startIcon={<Edit />} onClick={() => navigate(`/campaign/edit/${campaignId}`)}>Edit</Button>
              <Button variant="contained" color="success" startIcon={<Send />} onClick={() => execute(campaignId)}>Execute</Button>
            </>
          )}
          {campaign.status !== "running" && (
            <Button variant="outlined" color="error" startIcon={<Delete />} onClick={handleDelete}>Delete</Button>
          )}
        </Stack>
      </Box>

      {/* Campaign Info */}
      <Section>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 2, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <h2 style={{ margin: 0 }}>{campaign.name}</h2>
            {campaign.description && <p style={{ color: "#666", marginTop: 8 }}>{campaign.description}</p>}
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip label={campaign.type.toUpperCase()} color={typeColors[campaign.type]} />
            <Chip label={campaign.status.toUpperCase()} color={statusColors[campaign.status]} />
          </Stack>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6}><small style={{ color: "#666" }}>Created By</small><p style={{ margin: "4px 0 0" }}>{campaign.createdByName || "Admin"}</p></Grid>
          <Grid item xs={6}><small style={{ color: "#666" }}>Created At</small><p style={{ margin: "4px 0 0" }}>{new Date(campaign.createdAt).toLocaleString("en-US", { timeZone: "Asia/Dhaka" })}</p></Grid>
        </Grid>
      </Section>

      {/* Stats - use totalTargeted from stats after execution, fall back to estimatedReach for drafts */}
      <StatsDisplay stats={{ ...campaign.stats, estimatedReach: campaign.targetAudience?.estimatedReach }} />

      {/* Channel Stats */}
      {(campaign.type === "push" || campaign.type === "both") && (
        <ChannelStats title="Push Notification Stats" {...campaign.stats?.push} />
      )}
      {(campaign.type === "sms" || campaign.type === "both") && (
        <ChannelStats title="SMS Stats" {...campaign.stats?.sms} />
      )}

      {campaign.status === "scheduled" && campaign.schedule?.sendAt && (
        <Section>
          <b>Scheduled</b> for {new Date(campaign.schedule.sendAt).toLocaleString("en-GB", { timeZone: "Asia/Dhaka", dateStyle: "medium", timeStyle: "short" })} (Bangladesh time).
        </Section>
      )}

      {/* Results: cost, payers, revenue, profit or loss (SMS, and push for push campaigns) */}
      {(campaign.type === "sms" || campaign.type === "both") && <CampaignResults campaign={campaign} channel="sms" />}
      {(campaign.type === "push" || campaign.type === "both") && <CampaignResults campaign={campaign} channel="push" />}

      {/* Target Audience */}
      <TargetAudienceDisplay targetAudience={campaign.targetAudience} />

      {/* Message Content */}
      <MessagePreview type={campaign.type} pushMessage={campaign.pushMessage} smsMessage={campaign.smsMessage} />
    </Box>
  );
};

export default CampaignDetails;
